/*
 * Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved.
 */

import type { PlaylistEntry } from './PlaylistController'
import { parseMrn } from '../util/mrn'

/**
 * The Audible asin lives on the audiobook entity's playParams id — but the wire
 * sometimes delivers it as a full MRN (`mrn:1.0:audible:audiobook:asin:B0…?…`)
 * rather than a bare asin. The SDK's `load({ asin })` wants the bare asin, so
 * unwrap an MRN to its `identifier` (same shape the sample path sanitizes).
 *
 * Lives in its own module (not `AudibleSubPlayer`) so the boot-eager transport
 * classifier can resolve an audiobook asin without statically pulling the heavy
 * Audible engine + SDK singleton onto the entry chunk.
 */
export function audiobookAsin(entry: PlaylistEntry): string | null {
    const entity = entry.entity
    if (entity.__typename !== 'Audiobook') return null
    const raw = entity.playParams?.id
    if (!raw) return null
    if (!raw.includes('mrn:1.0:')) return raw
    try {
        return parseMrn(raw).identifier
    } catch {
        return raw
    }
}
